import { useState } from "react";
import { Link } from "react-router-dom";
import { Save } from "lucide-react";
import { L, cls, StatusSelect } from "./shared";

const SAVE = "inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800";

export function RegionForm({ form, setForm, onSubmit, onCancel }) {
  return (
    <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-md p-4 flex items-end gap-3 flex-wrap" data-testid="reg-form">
      <L label="Bölge Adı"><input required data-testid="reg-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} placeholder="örn. 1. Bölge" /></L>
      <L label="Durum"><StatusSelect value={form.status} onChange={(v) => setForm({ ...form, status: v })} /></L>
      <div className="flex gap-2">
        <button type="submit" data-testid="reg-save" className={SAVE}><Save className="w-3.5 h-3.5" /> Kaydet</button>
        <button type="button" onClick={onCancel} className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900">İptal</button>
      </div>
    </form>
  );
}

// Başlık Ekle: Hizmetler kataloğundaki (bu projede henüz açık olmayan) ana hizmetlerden seçim — işaretli alt hizmetler projeye açılır, işaretsizler bu projede gizlenir
export function CatPicker({ categories, services, projectId, busy, onAdd, onCancel }) {
  const [sel, setSel] = useState("");
  const [checked, setChecked] = useState({});
  const visible = (x) => x.scope !== "projects" || (x.project_ids || []).includes(projectId);
  // alt hizmetler: kalemler üst hizmetin hemen altında (↳)
  const subsOf = (catId) => {
    const rows = services.filter((s) => s.category_id === catId && s.status !== "Pasif" && (!s.project_id || s.project_id === projectId));
    const key = (s) => (s.parent_id ? `${rows.find((p) => p.id === s.parent_id)?.name || ""}\u0001${s.name}` : s.name);
    return rows.sort((a, b) => key(a).localeCompare(key(b), "tr"));
  };
  const options = categories.filter((c) => c.status !== "Pasif" && !visible(c)).sort((a, b) => a.name.localeCompare(b.name, "tr"));
  const cat = options.find((c) => c.id === sel);
  const subs = cat ? subsOf(cat.id) : [];
  const isOn = (s) => checked[s.id] ?? visible(s);
  const picked = subs.filter(isOn);
  const pick = (id) => { setSel(id); setChecked({}); };
  const setAll = (on) => setChecked(Object.fromEntries(subs.map((s) => [s.id, on])));
  const onKeyDown = (e) => { if (e.key === "Escape") { e.preventDefault(); onCancel(); } };
  return (
    <div onKeyDown={onKeyDown} className="bg-white border-2 border-slate-800 rounded-md p-4 space-y-3" data-testid="cat-form">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Ana Hizmet Ekle <span className="font-normal normal-case text-slate-400">— Hizmetler menüsündeki ana hizmetlerden seçin; işaretli alt hizmetler projeye açılır · ESC ile kapat</span></div>
      {options.length === 0 ? (
        <div className="text-xs text-slate-600" data-testid="cat-picker-empty">Katalogdaki tüm ana hizmetler bu projede zaten açık. Yeni bir ana hizmet için <Link to="/tanimlar/hizmetler" className="font-semibold text-amber-800 underline" data-testid="cat-picker-go-services">Hizmetler menüsünü</Link> kullanın.</div>
      ) : (
        <div className="flex items-end gap-3 flex-wrap">
          <L label="Ana Hizmet">
            <select autoFocus value={sel} onChange={(e) => pick(e.target.value)} className={cls} data-testid="cat-picker-select">
              <option value="">Seçin…</option>
              {options.map((c) => <option key={c.id} value={c.id}>{c.name} ({subsOf(c.id).length} alt hizmet)</option>)}
            </select>
          </L>
          <div className="flex gap-2">
            <button type="button" disabled={!cat || busy} onClick={() => onAdd(cat, picked, subs.filter((s) => !isOn(s)))} data-testid="cat-save" className={`${SAVE} disabled:opacity-50`}><Save className="w-3.5 h-3.5" /> Projeye Ekle{cat ? ` (${picked.length}/${subs.length} alt hizmet)` : ""}</button>
            <button type="button" onClick={onCancel} data-testid="cat-cancel" className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900">İptal (ESC)</button>
          </div>
        </div>
      )}
      {cat && (
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="cat-picker-preview">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{cat.name} <span className="font-normal normal-case text-slate-400">— alt hizmetler: işaretli olanlar projeye açılır ({picked.length}/{subs.length})</span></div>
            {subs.length > 1 && <span className="ml-auto flex gap-1">
              <button type="button" onClick={() => setAll(true)} data-testid="cat-picker-all" className="px-2 py-0.5 rounded border border-slate-300 bg-white text-[10px] font-semibold text-slate-700 hover:bg-slate-100">Tümünü Seç</button>
              <button type="button" onClick={() => setAll(false)} data-testid="cat-picker-none" className="px-2 py-0.5 rounded border border-slate-300 bg-white text-[10px] font-semibold text-slate-700 hover:bg-slate-100">Hiçbiri</button>
            </span>}
          </div>
          {subs.length === 0 ? <div className="text-xs text-slate-500 mt-1">Bu ana hizmetin kataloğunda alt hizmet yok; ekledikten sonra Fiyatlar sekmesinden alt hizmet açabilirsiniz.</div>
            : <ul className="mt-1.5 flex flex-wrap gap-1.5">{subs.map((s) => (
              <li key={s.id}>
                <label className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] cursor-pointer select-none ${isOn(s) ? "bg-white border-emerald-400 text-slate-800" : "bg-slate-100 border-slate-200 text-slate-400 line-through"}`} title={isOn(s) ? "Projeye açılır" : "Bu projede gizlenir"}>
                  <input type="checkbox" checked={isOn(s)} onChange={(e) => setChecked((c) => ({ ...c, [s.id]: e.target.checked }))} data-testid={`cat-picker-sub-${s.id}`} className="accent-emerald-600" />
                  {s.parent_id && <span className="text-slate-400">↳</span>}{s.name} <span className="text-slate-400 no-underline">· {s.unit}</span>
                </label>
              </li>
            ))}</ul>}
        </div>
      )}
    </div>
  );
}
